import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { MenuIcon, type MenuIconName } from './MenuIcons.js';
import type { Folder } from '../../api/folders.js';
import type { ReportTemplateSummary } from '../../api/types.js';

/**
 * FolderTree — a Jasper / SSRS / Crystal-style explorer.
 *
 * Folders expand/collapse; templates sit *inside* their folder as
 * leaves, not in a separate list underneath. Each row has a hover
 * kebab menu with the operator actions (rename, move, delete for
 * folders; rename, move, clone, delete for templates) so the sidebar
 * is the primary navigation + mgmt surface.
 *
 * Everything is derived from the flat folder list + flat template list;
 * no denormalised tree is fetched. Empty branches are collapsed so the
 * 60+ customer env-tree shells don't crowd the view.
 */

export interface FolderTreeProps {
  folders: ReadonlyArray<Folder>;
  templates: ReadonlyArray<ReportTemplateSummary>;
  selectedTemplateId: string | null;
  onSelectTemplate(id: string): void;
  expanded: ReadonlySet<string>;
  onToggle(id: string): void;
  searchQuery?: string;

  // Actions
  onRenameFolder(folder: Folder): void;
  onMoveFolder(folder: Folder): void;
  onDeleteFolder(folder: Folder): void;
  onNewFolder(parent: Folder | null): void;

  onRenameTemplate(template: ReportTemplateSummary): void;
  onMoveTemplate(template: ReportTemplateSummary): void;
  onCloneTemplate(template: ReportTemplateSummary): void;
  onDeleteTemplate(template: ReportTemplateSummary): void;
  /** Reorder a template up or down within its folder. */
  onReorderTemplate(template: ReportTemplateSummary, direction: 'up' | 'down'): void;
  /** Reorder a folder up or down within its parent. */
  onReorderFolder(folder: Folder, direction: 'up' | 'down'): void;
}

interface FolderNode {
  kind: 'folder';
  folder: Folder;
  children: TreeNode[];
  directCount: number;
  subtreeCount: number;
}
interface TemplateNode {
  kind: 'template';
  template: ReportTemplateSummary;
}
type TreeNode = FolderNode | TemplateNode;

export function FolderTree(props: FolderTreeProps): JSX.Element {
  const {
    folders,
    templates,
    selectedTemplateId,
    onSelectTemplate,
    expanded,
    onToggle,
    searchQuery,
    onRenameFolder,
    onMoveFolder,
    onDeleteFolder,
    onNewFolder,
    onReorderFolder,
    onRenameTemplate,
    onMoveTemplate,
    onCloneTemplate,
    onDeleteTemplate,
    onReorderTemplate,
  } = props;

  const tree = useMemo(
    () => buildTree(folders, templates, searchQuery ?? ''),
    [folders, templates, searchQuery],
  );
  const forceExpanded = useMemo<ReadonlySet<string>>(
    () => computeAutoExpand(folders, templates, searchQuery ?? ''),
    [folders, templates, searchQuery],
  );

  return (
    <div className="folder-tree" role="tree">
      <div className="folder-tree-row folder-tree-row--root">
        <span className="folder-tree-chevron folder-tree-chevron--static">
          <FolderIcon open />
        </span>
        <span className="folder-tree-label">
          <span className="folder-tree-name">All templates</span>
          <span className="folder-tree-count">{templates.length}</span>
        </span>
        <RowMenu
          items={[
            { label: 'New folder at top', icon: 'folder-plus', action: () => onNewFolder(null) },
          ]}
        />
      </div>

      <ul className="folder-tree-list">
        {tree.rootNodes.map((n) => (
          <TreeRow
            key={keyFor(n)}
            node={n}
            depth={0}
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={onSelectTemplate}
            expanded={expanded}
            forceExpanded={forceExpanded}
            onToggle={onToggle}
            onRenameFolder={onRenameFolder}
            onMoveFolder={onMoveFolder}
            onDeleteFolder={onDeleteFolder}
            onNewFolder={onNewFolder}
            onReorderFolder={onReorderFolder}
            onRenameTemplate={onRenameTemplate}
            onMoveTemplate={onMoveTemplate}
            onCloneTemplate={onCloneTemplate}
            onDeleteTemplate={onDeleteTemplate}
            onReorderTemplate={onReorderTemplate}
          />
        ))}
      </ul>
    </div>
  );
}

// ── Tree rendering ────────────────────────────────────────────────────────

interface TreeRowProps extends Omit<FolderTreeProps, 'folders' | 'templates' | 'searchQuery'> {
  node: TreeNode;
  depth: number;
  forceExpanded: ReadonlySet<string>;
}

function TreeRow(props: TreeRowProps): JSX.Element {
  if (props.node.kind === 'template') return <TemplateLeaf {...props} node={props.node} />;
  return <FolderRow {...props} node={props.node} />;
}

function FolderRow({
  node,
  depth,
  expanded,
  forceExpanded,
  onToggle,
  selectedTemplateId,
  onSelectTemplate,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
  onNewFolder,
  onReorderFolder,
  onRenameTemplate,
  onMoveTemplate,
  onCloneTemplate,
  onDeleteTemplate,
  onReorderTemplate,
}: TreeRowProps & { node: FolderNode }): JSX.Element {
  const f = node.folder;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(f.id) || forceExpanded.has(f.id);
  const indent: CSSProperties = { paddingLeft: `${8 + depth * 14}px` };

  // Hide branches that contain nothing useful (empty env shells).
  if (node.subtreeCount === 0) return <></>;

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
      <div className="folder-tree-row" style={indent}>
        <button
          type="button"
          className="folder-tree-chevron"
          onClick={() => hasChildren && onToggle(f.id)}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          disabled={!hasChildren}
        >
          {hasChildren ? (
            isOpen ? (
              <ChevronDown />
            ) : (
              <ChevronRight />
            )
          ) : (
            <span className="folder-tree-dot">·</span>
          )}
        </button>
        <button
          type="button"
          className="folder-tree-label folder-tree-label--folder"
          onClick={() => hasChildren && onToggle(f.id)}
          title={f.name}
        >
          <span className="folder-tree-icon">
            <FolderIcon open={isOpen} />
          </span>
          <span className="folder-tree-name">{f.name}</span>
          <span className="folder-tree-count">{node.subtreeCount}</span>
        </button>
        <RowMenu
          items={[
            { label: 'New folder inside', icon: 'folder-plus', action: () => onNewFolder(f) },
            { label: 'Move up', icon: 'move', action: () => onReorderFolder(f, 'up') },
            { label: 'Move down', icon: 'move', action: () => onReorderFolder(f, 'down') },
            { label: 'Rename…', icon: 'rename', action: () => onRenameFolder(f) },
            { label: 'Move…', icon: 'move', action: () => onMoveFolder(f) },
            {
              label: 'Delete folder',
              icon: 'delete',
              action: () => onDeleteFolder(f),
              destructive: true,
            },
          ]}
        />
      </div>
      {isOpen && hasChildren && (
        <ul className="folder-tree-list">
          {node.children.map((c) => (
            <TreeRow
              key={keyFor(c)}
              node={c}
              depth={depth + 1}
              expanded={expanded}
              forceExpanded={forceExpanded}
              onToggle={onToggle}
              selectedTemplateId={selectedTemplateId}
              onSelectTemplate={onSelectTemplate}
              onRenameFolder={onRenameFolder}
              onMoveFolder={onMoveFolder}
              onDeleteFolder={onDeleteFolder}
              onNewFolder={onNewFolder}
              onReorderFolder={onReorderFolder}
              onRenameTemplate={onRenameTemplate}
              onMoveTemplate={onMoveTemplate}
              onCloneTemplate={onCloneTemplate}
              onDeleteTemplate={onDeleteTemplate}
              onReorderTemplate={onReorderTemplate}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function TemplateLeaf({
  node,
  depth,
  selectedTemplateId,
  onSelectTemplate,
  onRenameTemplate,
  onMoveTemplate,
  onCloneTemplate,
  onDeleteTemplate,
  onReorderTemplate,
}: TreeRowProps & { node: TemplateNode }): JSX.Element {
  const t = node.template;
  const isSelected = selectedTemplateId === t.id;
  const indent: CSSProperties = { paddingLeft: `${24 + depth * 14}px` };
  return (
    <li role="treeitem" aria-selected={isSelected}>
      <div
        className={`folder-tree-row folder-tree-row--leaf ${isSelected ? 'folder-tree-row--selected' : ''}`}
        style={indent}
      >
        <button
          type="button"
          className="folder-tree-label folder-tree-label--template"
          onClick={() => onSelectTemplate(t.id)}
          title={
            t.description ? `${stripJasperTag(t.name)}\n\n${t.description}` : stripJasperTag(t.name)
          }
        >
          <span className="folder-tree-icon folder-tree-icon--template">
            <DocumentIcon />
          </span>
          <span className="folder-tree-name">{stripJasperTag(t.name)}</span>
        </button>
        <RowMenu
          items={[
            { label: 'Open', icon: 'open', action: () => onSelectTemplate(t.id) },
            { label: 'Move up', icon: 'move', action: () => onReorderTemplate(t, 'up') },
            { label: 'Move down', icon: 'move', action: () => onReorderTemplate(t, 'down') },
            { label: 'Rename…', icon: 'rename', action: () => onRenameTemplate(t) },
            { label: 'Move to…', icon: 'move', action: () => onMoveTemplate(t) },
            { label: 'Clone as report…', icon: 'clone', action: () => onCloneTemplate(t) },
            {
              label: 'Delete template',
              icon: 'delete',
              action: () => onDeleteTemplate(t),
              destructive: true,
            },
          ]}
        />
      </div>
    </li>
  );
}

// ── Hover kebab menu ─────────────────────────────────────────────────────

interface MenuItem {
  label: string;
  action(): void;
  destructive?: boolean;
  icon?: MenuIconName;
}

/**
 * Hover-kebab menu rendered via a React portal to document.body.
 *
 * Why the portal: the folder tree lives in a left panel with
 * `overflow: auto` so long lists scroll. An in-tree absolute popover
 * gets clipped by that scroll container regardless of z-index — that
 * was CSAGE-94. Portaling out of the tree subtree puts the popover
 * directly under <body> where no ancestor can clip it; we then
 * fixed-position it next to the trigger using getBoundingClientRect.
 *
 * Trade-off: the popover doesn't move with the row when the tree
 * scrolls, so we close on scroll/resize to avoid stale positioning.
 */
function RowMenu({ items }: { items: ReadonlyArray<MenuItem> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), []);

  // Position the popover relative to the trigger after open. Uses
  // useLayoutEffect so the popover is positioned before paint —
  // avoids a one-frame flash at (0,0).
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const popWidth = 160; // approx; matches min-width + padding
    setPos({ top: r.bottom + 2, left: Math.max(8, r.right - popWidth) });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      const tgt = e.target as globalThis.Node | null;
      if (tgt && popRef.current?.contains(tgt)) return;
      if (tgt && btnRef.current?.contains(tgt)) return;
      close();
    }
    function onEsc(e: KeyboardEvent): void {
      if (e.key === 'Escape') close();
    }
    // Close on scroll / resize so the absolute position can't go stale.
    function onLayoutChange(): void {
      close();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', onLayoutChange, true);
    window.addEventListener('resize', onLayoutChange);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', onLayoutChange, true);
      window.removeEventListener('resize', onLayoutChange);
    };
  }, [open, close]);

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popRef}
            className="folder-tree-menu-pop folder-tree-menu-pop--portaled"
            role="menu"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
          >
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className={`folder-tree-menu-item ${it.destructive ? 'folder-tree-menu-item--danger' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                  it.action();
                }}
              >
                <span className="folder-tree-menu-icon">
                  {it.icon ? <MenuIcon name={it.icon} /> : null}
                </span>
                <span>{it.label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="folder-tree-menu">
      <button
        ref={btnRef}
        type="button"
        className="folder-tree-menu-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label="Actions"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
          <circle cx="3" cy="7" r="1.3" />
          <circle cx="7" cy="7" r="1.3" />
          <circle cx="11" cy="7" r="1.3" />
        </svg>
      </button>
      {popover}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────

function FolderIcon({ open }: { open: boolean }): JSX.Element {
  return open ? (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <path
        d="M1.5 4.5a1.5 1.5 0 0 1 1.5-1.5h3.1a1.5 1.5 0 0 1 1.05.43L8.41 4.5H13a1.5 1.5 0 0 1 1.5 1.5v.5H1.5v-2z"
        opacity="0.55"
      />
      <path d="M1.5 6.5h13l-1.13 5.4a1.5 1.5 0 0 1-1.47 1.1H4.1a1.5 1.5 0 0 1-1.47-1.1L1.5 6.5z" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <path d="M1.5 5a1.5 1.5 0 0 1 1.5-1.5h3.1a1.5 1.5 0 0 1 1.05.43L8.41 5H13a1.5 1.5 0 0 1 1.5 1.5v5.5a1.5 1.5 0 0 1-1.5 1.5H3a1.5 1.5 0 0 1-1.5-1.5V5z" />
    </svg>
  );
}
function DocumentIcon(): JSX.Element {
  return (
    <svg
      width="12"
      height="14"
      viewBox="0 0 12 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    >
      <path d="M2 1.5h5.5L10.5 4.5V12a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V2.5a1 1 0 0 1 1-1z" />
      <path d="M7.5 1.5V4.5H10.5" />
    </svg>
  );
}
function ChevronRight(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 2l3 3-3 3" />
    </svg>
  );
}
function ChevronDown(): JSX.Element {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 4l3 3 3-3" />
    </svg>
  );
}

// ── Tree assembly ─────────────────────────────────────────────────────────

interface BuiltTree {
  rootNodes: TreeNode[];
}

function buildTree(
  folders: ReadonlyArray<Folder>,
  templates: ReadonlyArray<ReportTemplateSummary>,
  searchQuery: string,
): BuiltTree {
  const q = searchQuery.trim().toLowerCase();
  const matches = (s: string): boolean => !q || s.toLowerCase().includes(q);

  // Build folder nodes.
  const folderNodes = new Map<string, FolderNode>();
  for (const f of folders) {
    folderNodes.set(f.id, {
      kind: 'folder',
      folder: f,
      children: [],
      directCount: 0,
      subtreeCount: 0,
    });
  }

  // Attach folder children under their parents, or collect top-level.
  const rootFolders: FolderNode[] = [];
  for (const f of folders) {
    const node = folderNodes.get(f.id)!;
    if (f.parentId && folderNodes.has(f.parentId)) {
      folderNodes.get(f.parentId)!.children.push(node);
    } else {
      rootFolders.push(node);
    }
  }

  // Attach template leaves to their folder (or a synthetic "top level"
  // bucket if they have no folder).
  const topLevelTemplateLeaves: TemplateNode[] = [];
  for (const t of templates) {
    if (!matches(t.name)) continue;
    const leaf: TemplateNode = { kind: 'template', template: t };
    if (t.folderId && folderNodes.has(t.folderId)) {
      const host = folderNodes.get(t.folderId)!;
      host.children.push(leaf);
      host.directCount += 1;
    } else {
      topLevelTemplateLeaves.push(leaf);
    }
  }

  // Subtree counts bottom-up.
  const computeSubtree = (n: FolderNode): number => {
    let total = n.directCount;
    for (const c of n.children) {
      if (c.kind === 'folder') total += computeSubtree(c);
    }
    n.subtreeCount = total;
    return total;
  };
  for (const r of rootFolders) computeSubtree(r);

  // Sort siblings: folders first, populated folders before empty ones,
  // then alphabetically; templates alphabetically.
  const sortSiblings = (arr: TreeNode[]): void => {
    arr.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      if (a.kind === 'folder' && b.kind === 'folder') {
        if (a.subtreeCount !== b.subtreeCount) return b.subtreeCount - a.subtreeCount;
        return a.folder.name.localeCompare(b.folder.name);
      }
      if (a.kind === 'template' && b.kind === 'template') {
        return a.template.name.localeCompare(b.template.name);
      }
      return 0;
    });
  };
  const sortAll = (n: FolderNode): void => {
    sortSiblings(n.children);
    for (const c of n.children) if (c.kind === 'folder') sortAll(c);
  };
  for (const r of rootFolders) sortAll(r);

  // Combine top-level folders + top-level template leaves.
  const rootNodes: TreeNode[] = [...rootFolders, ...topLevelTemplateLeaves];
  sortSiblings(rootNodes);

  return { rootNodes };
}

function computeAutoExpand(
  folders: ReadonlyArray<Folder>,
  templates: ReadonlyArray<ReportTemplateSummary>,
  searchQuery: string,
): ReadonlySet<string> {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return new Set();

  const byId = new Map<string, Folder>();
  for (const f of folders) byId.set(f.id, f);
  const ancestors = (id: string | null): string[] => {
    const out: string[] = [];
    let cursor: string | null = id;
    while (cursor) {
      out.push(cursor);
      const f = byId.get(cursor);
      cursor = f ? f.parentId : null;
    }
    return out;
  };

  const open = new Set<string>();
  for (const t of templates) {
    if (t.name.toLowerCase().includes(q)) {
      for (const a of ancestors(t.folderId)) open.add(a);
    }
  }
  for (const f of folders) {
    if (f.name.toLowerCase().includes(q)) {
      for (const a of ancestors(f.id)) open.add(a);
    }
  }
  return open;
}

function keyFor(n: TreeNode): string {
  return n.kind === 'folder' ? `f:${n.folder.id}` : `t:${n.template.id}`;
}

function stripJasperTag(name: string): string {
  return name.replace(/\s*\[Jasper #\d+\]\s*$/, '');
}

// ── Exported helpers ──────────────────────────────────────────────────────

export function isUnderFolder(
  folderId: string | null,
  rootId: string,
  foldersById: Map<string, Folder>,
): boolean {
  if (!folderId) return false;
  let cursor: string | null = folderId;
  while (cursor) {
    if (cursor === rootId) return true;
    const f: Folder | undefined = foldersById.get(cursor);
    cursor = f ? f.parentId : null;
  }
  return false;
}
